/**
 * Crew — Liste et gestion de l'équipage IA
 */

import { useState, useEffect } from "react";
import { api } from "../lib/api";
import type { CrewMember } from "@synapse/shared";
import { showToast } from "../components/Toast";

const ROLE_LABELS: Record<string, string> = {
  navigator: "Navigateur",
  cervelleau: "Cerveau",
  programmeur: "Programmeur",
  securite: "Sécurité",
  createur: "Créateur",
};

const ROLES = ["navigator", "cervelleau", "programmeur", "securite", "createur"] as const;
const PROVIDERS = ["zai", "openai"] as const;

export default function Crew() {
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setCrew(await api.crew.list());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activeCount = crew.filter((c) => c.active).length;

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">équipage IA</div>
          <h1>Équipage</h1>
          <p>{crew.length} agent{crew.length > 1 ? "s" : ""} · {activeCount} actif{activeCount > 1 ? "s" : ""}</p>
        </div>
        <div className="header-actions">
          <button className="btn primary" onClick={() => setShowModal(true)}>+ Nouvel agent</button>
        </div>
      </div>

      {error && <div className="ops-card" style={{ borderColor: "var(--rust)" }}><p style={{ color: "var(--rust)" }}>⚠ {error}</p></div>}
      {loading && <div className="no-results">Chargement…</div>}

      {!loading && !error && (
        <div className="crew-grid">
          {crew.map((member) => (
            <div key={member.id} className="crew-card">
              <div className="crew-header">
                <div className="crew-avatar" style={{ background: member.avatarColor || "var(--purple)" }}>
                  {member.avatar || "🤖"}
                </div>
                <div>
                  <strong>{member.name}</strong><br />
                  <span>{ROLE_LABELS[member.role] || member.role}</span>
                </div>
              </div>
              {member.description && (
                <div className="crew-desc">{member.description}</div>
              )}
              <div className="crew-meta">
                <span className="tag">{member.llmProvider} · {member.llmModel}</span>
                {member.capabilities?.map((cap) => (
                  <span key={cap} className="tag">{cap}</span>
                ))}
              </div>
              <div className="toggle-row">
                <span>{member.active ? "🟢 Actif" : "⏸ Inactif"}</span>
              </div>
            </div>
          ))}
          {crew.length === 0 && !loading && (
            <div className="no-results">Aucun agent. Ajoute le premier !</div>
          )}
        </div>
      )}

      {showModal && (
        <NewCrewModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Modal de création d'agent (VAI-01) ──────────────────────────

function NewCrewModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState<(typeof ROLES)[number]>("programmeur");
  const [provider, setProvider] = useState<(typeof PROVIDERS)[number]>("zai");
  const [model, setModel] = useState("glm-4.7");
  const [description, setDescription] = useState("");
  const [avatar, setAvatar] = useState("🤖");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      showToast("error", "Nom requis", "Renseigne un nom pour l'agent.");
      return;
    }
    setSaving(true);
    try {
      await api.crew.create({
        name: name.trim(),
        role,
        llmProvider: provider,
        llmModel: model.trim() || "glm-4.7",
        description: description.trim(),
        avatar: avatar || "🤖",
      });
      showToast("success", "Agent créé", `${name.trim()} (${ROLE_LABELS[role]}) ajouté à l'équipage.`);
      onCreated();
    } catch (err) {
      showToast("error", "Échec création", err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="cmd-question-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "460px", maxWidth: "92vw", background: "rgba(18,22,41,0.98)",
          border: "1px solid var(--line)", borderRadius: "20px", padding: "20px",
          display: "grid", gap: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        <strong style={{ fontSize: "16px" }}>➕ Nouvel agent</strong>

        <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
          Nom
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Ada" />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: "10px" }}>
          <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
            Rôle
            <select className="field-input" value={role} onChange={(e) => setRole(e.target.value as (typeof ROLES)[number])}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </label>
          <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
            Avatar
            <input className="field-input" value={avatar} onChange={(e) => setAvatar(e.target.value)} maxLength={2} />
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
            Provider LLM
            <select className="field-input" value={provider} onChange={(e) => setProvider(e.target.value as (typeof PROVIDERS)[number])}>
              {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
            Modèle
            <input className="field-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="glm-4.7" />
          </label>
        </div>

        <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
          Description
          <textarea className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Spécialité de l'agent…" />
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
          <button className="btn" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn primary" onClick={submit} disabled={saving}>
            {saving ? "⟳ Création…" : "Créer l'agent"}
          </button>
        </div>
      </div>
    </div>
  );
}
